import './FileDropZone.scss';
import cn from 'classnames';
import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { InteractiveDiv } from '@Components/InteractiveEl';

interface FileDropZoneProps {
  // Filters the picker only; drag-and-drop and the "All files" option both bypass it, so a
  // caller that cares must also check the file in getValidationError.
  accept?: string;
  getValidationError?: (file: File) => string | null;
  onFile: (file: File) => void;
  onError?: (message: string) => void;
  hintText?: string;
  className?: string;
}

export const FileDropZone = ({
  accept,
  getValidationError,
  onFile,
  onError,
  hintText,
  className
}: FileDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectFile = (file: File | undefined) => {
    if (!file) return;
    const error = getValidationError?.(file);
    if (error) {
      onError?.(error);
      return;
    }
    onFile(file);
  };

  return (
    <InteractiveDiv
      className={cn('file-drop-zone', className, { dragging: isDragging })}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e: DragEvent<HTMLDivElement>) => {
        // Moving onto a child still counts as being inside the zone.
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) {
          return;
        }
        setIsDragging(false);
      }}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        selectFile(e.dataTransfer.files?.[0]);
      }}
    >
      <div className='drop-zone-text'>
        Drag a file here, or <span className='drop-zone-browse'>browse</span>
      </div>
      {hintText && <div className='drop-zone-hint'>{hintText}</div>}
      <input
        ref={inputRef}
        type='file'
        accept={accept}
        className='hidden-file-input'
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          selectFile(e.target.files?.[0]);
          // Clear it so re-picking the same file fires another change event -- otherwise a
          // rejected file cannot be retried.
          e.target.value = '';
        }}
      />
    </InteractiveDiv>
  );
};
